import { useDataProvider } from "../../../data_provider/StandardDataProvider"

import { memo, useEffect, useMemo, useState } from "react"
import { endOfDay, format as formatDate, formatDistance, startOfDay } from "date-fns"
import { useOutletContext } from "react-router-dom"
import { Alert, Badge, Card, Center, Divider, Grid, Group, JsonInput, Loader, ScrollArea, Stack, Text, Textarea } from "@mantine/core"
import BaseChart from "../../../charts/BaseChart"
import { colorSelect2 } from "./utils"
import { useInputDispatch } from "../../../../hooks/inputDispatch"
import _ from "lodash"
import axios from "axios"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faExternalLink } from "@fortawesome/free-solid-svg-icons"
import { formatInTimeZone } from "date-fns-tz"


const CACHE_MAX_SIZE = 10


export const EventDetailedView = ({selectedEvent}) => {

    const {
        data: data,
        requestId: requestId,
        isDataReady: isDataReady,
        isLoading: isLoading,
        isError: isError,
        errorMessage: errorMessage
    } = useEventQueryCached(selectedEvent)

    const [description, updateDescription] = useDescriptionUpdateCache(selectedEvent)


    return (
        <Card withBorder style={{height: "100%"}}>
            {!selectedEvent && <Center style={{height: "100%"}}><Text>Select an event.</Text></Center>}
            {selectedEvent &&
            <Stack pb={0} spacing={0} style={{height: "100%", overflow: "auto"}}>
                <EventTrendSection
                    event={selectedEvent}
                    data={data}
                    requestId={requestId}
                    isDataReady={isDataReady}
                    isLoading={isLoading}
                    isError={isError}
                    errorMessage={errorMessage}
                />
                <Divider />
                <EventAttributeSection event={selectedEvent} description={description} updateDescription={updateDescription} />
            </Stack>}
        </Card>
    )
}

const EventTrendSection = memo(({event, data, requestId, isDataReady, isLoading, isError, errorMessage}) => {
    return (
        <Card.Section className="flow-vertical" p={8} style={{flexGrow: 1, flexShrink: 1, minHeight: "300px"}}>
            <Group pb={30} align="baseline">
                <Text weight={500} underline>Event Trend</Text>
                {isDataReady &&
                    <Text size="xs" variant="link" target="_blank" component="a" href={buildEventTrendLink(event, data)} style={{fontSize: "11px"}}>
                        Open in Trends
                        <FontAwesomeIcon icon={faExternalLink} style={{marginLeft: "4px"}} />
                    </Text>
                }
            </Group>
            {isLoading && <Center style={{height: "100%"}}><Loader variant="bars" color="red" /></Center>}
            {isError && !isLoading && <Center mt={15}><Alert color="red">{errorMessage}</Alert></Center>}
            {isDataReady && <EventTrend event={event} requestId={requestId} data={data} />}
        </Card.Section>
    )
})

const EventTrend = ({event, requestId, data}) => {

    const { assetFrameworkServices } = useOutletContext()

    return (
        <BaseChart
            key={event?._id}
            services={{assetFramework: assetFrameworkServices}}
            timezone={assetFrameworkServices.getSites().find(x => x._id===event.assets.site).meta.timezone}
            dataIsRaw={true}
            data={data}
            style={{height: "100%"}}
            dataRequestId={requestId}
            dataExportTitle={`Event ${event._id}`}
        />
    )
}

const EventAttributeSection = ({event, description, updateDescription}) => {

    /*
    *maybe: Add reference to blueprint cobject
    */

    const { assetFrameworkServices } = useOutletContext()
    const site = assetFrameworkServices.getSites(event.assets?.site)
    const timezone = site?.meta?.timezone || "UTC"

    const openedTs = new Date(event.opened_ts)
    const closedTs = new Date(event.closed_ts)
    const openedTsFormatted = !isNaN(openedTs) ? formatInTimeZone(openedTs, timezone, "EEE, MMM dd uuuu, hh:mm a") : "Invalid Date"
    const closedTsFormatted = !isNaN(closedTs) ? formatInTimeZone(closedTs, timezone, "EEE, MMM dd uuuu, hh:mm a") : (event.status==="open" ? null : "Invalid Date")
    const durationFormatted = formatDistance(event.status==="open" ? new Date() : closedTs, openedTs)
    

    return (
        <Stack spacing="md" px={10} pt={10} style={{maxHeight: "250px", minHeight: "250px", flexShrink: 1}}>
            <Group>
                <Text underline weight={500}>Event</Text>
                <Badge size="sm" variant="filled" color={event.blueprint?.template?.color ? event.blueprint.template.color : "gray"}>{event.blueprint.template.code}</Badge>
                <Badge size="sm" variant="outline" color={colorSelect2(event.status)}>{event.status}</Badge>
            </Group>

            <Group noWrap align="flex-start" spacing="xs" style={{height: "198px"}}>
                <Grid align="flex-start">
                    <Grid.Col md={12} lg={6}>
                        <Grid>
                            <Grid.Col span={5}><Text size="xs" weight={500}>Event ID</Text></Grid.Col>
                            <Grid.Col span={7}><Text size="xs" style={{whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>{event._id}</Text></Grid.Col>
                            <Grid.Col span={5}><Text size="xs" weight={500}>Duration</Text></Grid.Col>
                            <Grid.Col span={7}><Text size="xs">{durationFormatted}</Text></Grid.Col>
                            <Grid.Col span={5}><Text size="xs" weight={500}>Opened At</Text></Grid.Col>
                            <Grid.Col span={7}><Text size="xs">{openedTsFormatted}</Text></Grid.Col>
                            <Grid.Col span={5}><Text size="xs" weight={500}>Closed At</Text></Grid.Col>
                            <Grid.Col span={7}><Text size="xs">{closedTsFormatted}</Text></Grid.Col>
                            <Grid.Col span={5}><Text size="xs" weight={500}>Device</Text></Grid.Col>
                            <Grid.Col span={7}><Text size="xs">{event.asset_name}</Text></Grid.Col>
                        </Grid>
                    </Grid.Col>

                    <Grid.Col md={12} lg={6}>
                        <Grid>
                            <Grid.Col span={5}><Text size="xs" weight={500}>Trigger Value</Text></Grid.Col>
                            <Grid.Col span={7}><Text size="xs">{String(event.trigger)}</Text></Grid.Col>
                            <Grid.Col span={5}><Text size="xs" weight={500}>Method</Text></Grid.Col>
                            <Grid.Col span={7}><Text size="xs">{event.method}</Text></Grid.Col>
                            <Grid.Col span={12}><Text size="xs" weight={500}>Description</Text></Grid.Col>
                            <Grid.Col span={12}>
                                <Textarea
                                    key={event._id}
                                    value={description}
                                    size="xs"
                                    minRows={3}
                                    onChange={(e) => updateDescription(e.currentTarget.value)}
                                />
                            </Grid.Col>
                        </Grid>
                    </Grid.Col>
                </Grid>

                <Divider orientation="vertical" mx={15} style={{height: "90%"}} />

                <Stack mr={15} style={{height: "100%", flexShrink: 0, maxWidth: "400px"}}>
                    <Text size="xs" weight={500}>Event Detail</Text>
                    <ScrollArea pb={16}>
                        <Text size="xs" style={{whiteSpace: "pre"}}>{getEventDetailUserContext(event)}</Text>
                    </ScrollArea>
                </Stack>
            </Group>
        </Stack>
    )
}

const getEventDetailUserContext = (event) => {
    try {
        const eventDetail = JSON.parse(event.detail)
        return JSON.stringify(eventDetail.user_context, null, 4)
    }
    catch {
        return event.detail
    }
}












const useEventQueryCached = (selectedEvent) => {

    console.log(selectedEvent)

    const result = useDataProvider({
        requestMethod: "GET",
        requestUrl: "/api/dashboard/tron/data/trend",
        buildRequestBody: () => Object({
            eventId: selectedEvent?._id,
            eventTemplateId: selectedEvent?.blueprint?.template?._id,
            eventSiteId: selectedEvent?.assets?.site,
            eventOpenedTime: selectedEvent?.opened_ts,
            eventClosedTime: selectedEvent?.closed_ts,
        }),
        resetDataOnRequest: false,
        // Update cache only when success response received
        //  Don't want to cache errors / loading state
        // Limit cache size to 10 entries
        onSuccessFn: (response, newRequestId) => {
            const eventId = response.config.params.eventId
            setCache((cache_) => {
                const next_cache = new Map(cache_.entries())
                // -1 b/c checking before adding next entry
                if (next_cache.size > CACHE_MAX_SIZE-1) next_cache.delete(Array.from(next_cache.keys())[next_cache.size-1])
                next_cache.set(eventId, {
                    data: response.data,
                    requestId: newRequestId,
                    isDataReady: true, isLoading: false, isError: false, errorMessage: false
                })
                return next_cache
            })
        }
    })

    const [cache, setCache] = useState(new Map())
    const [activeCache, setActiveCache] = useState({})

    const queryCache = () => {
        const eventId = selectedEvent._id
        if (cache.has(eventId)) setActiveCache(cache.get(eventId))
        else {
            result.refreshData()
            setActiveCache({})
        }
    }

    useEffect(() => {
        if (selectedEvent) queryCache()
        else setCache(new Map())
    }, [selectedEvent])


    return {...result, ...activeCache}
}

const useDescriptionUpdateCache = (event) => {

    const eventId = event ? event._id : null

    const dispatch = useInputDispatch(1500)

    const [descriptionCache, setDescriptionCache] = useState({})
    const updateDescription = (description) => {
        descriptionCache[eventId].staged = description
        const onSaveFn = () => saveDescription(eventId, description)
        dispatch(onSaveFn)
    }

    const saveDescription = (eventId, description) => {
        axios.post("/api/event/update/description", {
            eventId: eventId, description, description
        })
        .then(() => {
            setDescriptionCache((cache) => {
                const newCache = _.cloneDeep(cache)
                newCache[eventId].committed = description
                return newCache
            })
        })
        .catch((error) => {
            console.error("Failed to update description", error)
            alert("Failed to update description")
        })
    }

    useEffect(() => {
        if (eventId && !(eventId in descriptionCache)) {
            setDescriptionCache((cache) => Object.assign({}, _.cloneDeep(cache), {[eventId]: {committed: event.description, staged: event.description}}))
        }
    }, [eventId])
    

    return [descriptionCache[eventId] ? descriptionCache[eventId].staged : null, updateDescription]
}

const buildEventTrendLink = (event, data) => {

    const startTs = startOfDay(new Date(event.opened_ts))
    const endTs = event.status==="closed" ? endOfDay(new Date(event.closed_ts)) : endOfDay(new Date())
    const startTsFormatted = !isNaN(startTs) ? formatDate(startTs, "uuuu-MM-dd HH:mm:ss").replace(" ", "T") : ""
    const endTsFormatted = !isNaN(endTs) ? formatDate(endTs, "uuuu-MM-dd HH:mm:ss").replace(" ", "T") : (event.status==="open" ? null : "")

    const deviceIds = data.map(x => Number(x.device))
    const sourceTagIds = data.filter(x => x.tag_type==="raw").map(x => Number(x.tag))
    const intervalTagIds = data.filter(x => x.tag_type==="interval").map(x => Number(x.tag))
    const deviceIdsFormatted = deviceIds.join(",")
    const sourceTagIdsFormatted = sourceTagIds.join(",")
    const intervalTagIdsFormatted = intervalTagIds.join(",")

    const host = window.location.host
    let link = `https://${host}/pharos/trends?start=${encodeURIComponent(startTsFormatted)}&end=${encodeURIComponent(endTsFormatted)}&res=0&site=${event.assets.site}`
    
    if (deviceIdsFormatted.length > 0) link+="&devices="+encodeURIComponent(deviceIdsFormatted)
    if (sourceTagIdsFormatted.length > 0) link+="&stags="+encodeURIComponent(sourceTagIdsFormatted)
    if (intervalTagIdsFormatted.length > 0) link+="&itags="+encodeURIComponent(intervalTagIdsFormatted)

    return link
}